import React, { useEffect, useState } from "react";

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 11v5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="7.75" r="1" fill="currentColor" />
  </svg>
);

// Modal for defining a custom companion document. Calls
// `onSave({ name, description })`; Save disabled until a name is entered.
const CustomDocumentModal = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // "idle" | "saving" | "saved"
  const [status, setStatus] = useState("idle");

  // Reset fields each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setName("");
      setDescription("");
      setStatus("idle");
    }
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isBusy = status === "saving" || status === "saved";
  const canSave = name.trim().length > 0 && !isBusy;

  const handleSave = async () => {
    if (name.trim().length === 0 || isBusy) return;
    setStatus("saving");
    try {
      if (onSave) await onSave({ name: name.trim(), description: description.trim() });
      setStatus("saved");
      // Briefly show "Saved", then close.
      setTimeout(() => onClose(), 700);
    } catch (error) {
      console.error("Failed to save custom document:", error);
      setStatus("idle"); // allow retry
    }
  };

  const saveLabel =
    status === "saving" ? "Saving…" : status === "saved" ? "Saved" : "Save";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] bg-white rounded-2xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-outfit text-[20px] font-semibold text-[#18101C]">
                Custom Companion Documents
              </h2>
              <p className="font-outfit text-[14px] text-[#767676] mt-0.5">
                Define a custom document for your proposal
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 -mt-1 text-[#6B7280] hover:text-[#18101C] transition-colors"
            >
              <CloseIcon />
            </button>
          </div>
          <div className="mt-4 border-t border-gray-200" />
        </div>

        <div className="px-6 py-5">
          <label className="block font-outfit text-[14px] font-semibold text-[#18101C] mb-2">
            Document Name<span className="text-[#A04A97]">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Training & Onboarding Plan"
            autoFocus
            className="w-full rounded-lg border border-gray-300 px-3.5 py-2.5 font-outfit !text-[14px] font-normal text-[#18101C] placeholder:text-[#666666] placeholder:text-[14px] placeholder:font-normal focus:outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE] transition-colors"
          />

          <label className="block font-outfit text-[14px] font-semibold text-[#18101C] mt-5 mb-2">
            Description{" "}
            <span className="font-normal text-[#9CA3AF]">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="e.g., This document will provide a detailed training and onboarding plan for end users and administrators."
            className="w-full resize-none rounded-lg border border-gray-300 px-3.5 py-2.5 font-outfit !text-[14px] font-normal leading-relaxed text-[#18101C] placeholder:text-[#666666] placeholder:text-[14px] placeholder:font-normal focus:outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE] transition-colors"
          />

          <div className="flex items-start gap-2 mt-4">
            <span className="text-[#858282] mt-[2px] flex-shrink-0">
              <InfoIcon />
            </span>
            <p className="font-outfit text-[13px] text-[#858282]">
              Enter a name and brief description. You can generate this document
              next and export it along with your proposal.
            </p>
          </div>
        </div>

        <div className="px-6 pb-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isBusy}
            className="px-5 py-2 rounded-lg border border-[#A04A97] text-[#A04A97] font-outfit text-[14px] font-medium hover:bg-[#C95EBE0D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`px-6 py-2 rounded-lg font-outfit text-[14px] font-medium text-white transition-colors ${
              status === "saved"
                ? "bg-[#C95EBE]"
                : canSave || isBusy
                ? "bg-[#C95EBE] hover:bg-[#BB42AE]"
                : "bg-[#C7C7CC] cursor-not-allowed"
            }`}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomDocumentModal;
